import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Company {
    id: string;
    name: string;
    companyCode?: string;
    legalName: string | null;
    currencyCode: string;
    ownerName?: string | null;
}

interface CompanyContextType {
    activeCompany: Company | null;
    setActiveCompany: (company: Company | null) => void;
    companies: Company[];
    setCompanies: (companies: Company[]) => void;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

const ACTIVE_COMPANY_KEY = 'books_active_company';

export const CompanyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [activeCompany, setActiveCompanyState] = useState<Company | null>(null);
    const [companies, setCompanies] = useState<Company[]>([]);

    // Load active company from localStorage on mount
    useEffect(() => {
        const saved = localStorage.getItem(ACTIVE_COMPANY_KEY);
        if (saved) {
            try {
                const company = JSON.parse(saved);
                setActiveCompanyState(company);
            } catch (e) {
                console.error('Failed to parse saved company', e);
            }
        }
    }, []);

    // Validate activeCompany when companies are loaded
    useEffect(() => {
        if (activeCompany && companies.length > 0) {
            const companyExists = companies.some(c => c.id === activeCompany.id);
            if (!companyExists) {
                console.warn(`Active company ${activeCompany.id} (${activeCompany.name}) not found in companies list. Clearing active company.`);
                setActiveCompanyState(null);
                localStorage.removeItem(ACTIVE_COMPANY_KEY);
            }
        }
    }, [companies, activeCompany]);

    // Save active company to localStorage whenever it changes
    const setActiveCompany = (company: Company | null) => {
        setActiveCompanyState(company);
        if (company) {
            localStorage.setItem(ACTIVE_COMPANY_KEY, JSON.stringify(company));
        } else {
            localStorage.removeItem(ACTIVE_COMPANY_KEY);
        }
    };

    // Wrapper for setCompanies that also validates activeCompany
    const setCompaniesWithValidation = (newCompanies: Company[]) => {
        setCompanies(newCompanies);
        
        // Validate activeCompany exists in the new companies list
        if (activeCompany && newCompanies.length > 0) {
            const companyExists = newCompanies.some(c => c.id === activeCompany.id);
            if (!companyExists) {
                console.warn(`Active company ${activeCompany.id} (${activeCompany.name}) not found in companies list. Clearing active company.`);
                setActiveCompanyState(null);
                localStorage.removeItem(ACTIVE_COMPANY_KEY);
            }
        }
    };

    return (
        <CompanyContext.Provider value={{ activeCompany, setActiveCompany, companies, setCompanies: setCompaniesWithValidation }}>
            {children}
        </CompanyContext.Provider>
    );
};

export const useCompanyContext = () => {
    const context = useContext(CompanyContext);
    if (context === undefined) {
        throw new Error('useCompanyContext must be used within a CompanyProvider');
    }
    return context;
};

